import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Keyboard, Search, Store } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useShop } from '@/contexts/ShopContext';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from '@/components/ui/command';
import {
  useCommandActions,
  type CommandActionDescriptor,
  type CommandActionGroup,
} from '@/hooks/useCommandActions';
import { ownerPath } from '@/lib/hosts';

type PaletteFilter = 'all' | 'tools' | 'pages';

/** Mapping filter → groupes inclus. */
const FILTER_GROUPS: Record<PaletteFilter, CommandActionGroup[]> = {
  all: ['navigation', 'create', 'shop', 'system', 'admin'],
  tools: ['create', 'system'],
  pages: ['navigation', 'shop'],
};

const GOTO_MAP: Record<string, string> = {
  d: '/dashboard',
  s: '/sales',
  p: '/products',
  k: '/stock',
  f: '/finances',
  r: '/finances',
  e: '/employees',
  c: '/customers',
};

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

function CommandRow({
  action,
  onSelect,
  showDescription,
}: {
  action: CommandActionDescriptor;
  onSelect: () => void;
  /** If true, render description on a second line under the label. */
  showDescription?: boolean;
}) {
  const Icon = action.icon;
  return (
    <CommandItem
      onSelect={onSelect}
      keywords={action.keywords}
      className={cn('min-h-10 sm:min-h-9', showDescription && action.hint && 'items-start py-2')}
    >
      <Icon
        className={cn(
          'mr-2 shrink-0',
          showDescription && action.hint ? 'mt-0.5 h-4 w-4' : 'h-4 w-4',
        )}
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-center gap-2">
          <span className="flex-1 truncate text-[13px]">{action.label}</span>
          {typeof action.badge === 'number' && action.badge > 0 ? (
            <span className="rounded-full bg-rose-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-rose-700 dark:bg-rose-500/15 dark:text-rose-300">
              {action.badge > 99 ? '99+' : action.badge}
            </span>
          ) : null}
        </span>
        {showDescription && action.hint ? (
          <span className="truncate text-[11px] leading-tight text-slate-500 dark:text-slate-400">
            {action.hint}
          </span>
        ) : null}
      </span>
      {!showDescription && action.hint && !action.badge ? (
        <span className="ml-2 hidden truncate text-[11px] text-charcoal-400 sm:inline dark:text-charcoal-500">
          {action.hint}
        </span>
      ) : null}
      {action.shortcut ? <CommandShortcut>{action.shortcut}</CommandShortcut> : null}
    </CommandItem>
  );
}

interface FilterTabsProps {
  value: PaletteFilter;
  onChange: (next: PaletteFilter) => void;
  labels: Record<PaletteFilter, string>;
}

function FilterTabs({ value, onChange, labels }: FilterTabsProps) {
  const tabs: PaletteFilter[] = ['all', 'tools', 'pages'];
  return (
    <div
      role="tablist"
      aria-label="Filtrer les résultats"
      className="flex items-center gap-1 border-b border-slate-100 px-3 py-2 dark:border-charcoal-800"
    >
      {tabs.map((tab) => {
        const active = value === tab;
        return (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab)}
            className={cn(
              'rounded-full px-3 py-1 text-[12px] font-medium transition-colors',
              active
                ? 'bg-lime-100 text-charcoal-950 dark:bg-lime-500/15 dark:text-lime-200'
                : 'text-slate-500 hover:bg-slate-100 hover:text-charcoal-900 dark:text-slate-400 dark:hover:bg-charcoal-800 dark:hover:text-slate-100',
            )}
          >
            {labels[tab]}
          </button>
        );
      })}
    </div>
  );
}

const ORDERED_GROUPS: Array<{ key: CommandActionGroup; heading: string }> = [
  { key: 'navigation', heading: 'Navigation' },
  { key: 'create', heading: 'Quick Create' },
  { key: 'system', heading: 'Actions' },
  { key: 'admin', heading: 'Admin' },
];

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<PaletteFilter>('all');
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation('common');
  const { currentShop, shops, selectShop } = useShop();
  const gotoBufferRef = useRef<{ active: boolean; timer: number | null }>({
    active: false,
    timer: null,
  });

  const { groups, quickActions, runAction } = useCommandActions({
    onAfterRun: () => setOpen(false),
  });

  // Reset filter/query whenever the palette closes so the next open is fresh.
  useEffect(() => {
    if (!open) {
      setFilter('all');
      setQuery('');
    }
  }, [open]);

  const filterLabels: Record<PaletteFilter, string> = {
    all: t('command_palette_filter_all', { defaultValue: 'Tout' }),
    tools: t('command_palette_filter_tools', { defaultValue: 'Outils' }),
    pages: t('command_palette_filter_pages', { defaultValue: 'Pages' }),
  };

  const allowedGroups = useMemo(() => new Set(FILTER_GROUPS[filter]), [filter]);
  const showShopSwitcher = filter !== 'tools';
  const showBrowseTools = filter === 'tools' && query.trim() === '';

  // Global keyboard shortcuts (preserved from previous version)
  useEffect(() => {
    const clearGoto = () => {
      gotoBufferRef.current.active = false;
      if (gotoBufferRef.current.timer !== null) {
        window.clearTimeout(gotoBufferRef.current.timer);
        gotoBufferRef.current.timer = null;
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K / Ctrl+K toggles palette (works inside inputs too)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
        clearGoto();
        return;
      }

      // Cmd+N / Ctrl+N → open POS (handle before edit-target guard so it works anywhere)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'n') {
        e.preventDefault();
        navigate(ownerPath('/pos'));
        setOpen(false);
        clearGoto();
        return;
      }

      // Skip non-modifier shortcuts when typing in editable fields or palette is open
      if (isEditableTarget(e.target) || open) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key.toLowerCase();

      // Chord G + letter: navigate
      if (gotoBufferRef.current.active) {
        const target = GOTO_MAP[key];
        if (target) {
          e.preventDefault();
          navigate(ownerPath(target));
        }
        clearGoto();
        return;
      }

      if (key === 'g') {
        e.preventDefault();
        gotoBufferRef.current.active = true;
        gotoBufferRef.current.timer = window.setTimeout(clearGoto, 1500);
        return;
      }

      // Single-letter shortcuts
      if (key === 'r') {
        e.preventDefault();
        void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        return;
      }
      if (key === 'l') {
        e.preventDefault();
        navigate(ownerPath('/finances'));
        return;
      }
      if (key === 'p') {
        e.preventDefault();
        navigate(ownerPath('/profile'));
        return;
      }
      if (key === '?') {
        e.preventDefault();
        setOpen(true);
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearGoto();
    };
  }, [navigate, queryClient, open]);

  const handleSelect = useCallback(
    (action: CommandActionDescriptor) => {
      void runAction(action);
    },
    [runAction],
  );

  // Build the "Browse all tools" list (state vide + filter tools) — fusion create + system, dédup quickAction.
  const browseToolsItems = useMemo(
    () => [...(groups.create ?? []), ...(groups.system ?? [])].filter((a) => !a.quickAction),
    [groups.create, groups.system],
  );

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        placeholder={t('search_placeholder', { defaultValue: 'Rechercher, créer, naviguer…' })}
        wrapperClassName="border-b border-slate-100 px-4 dark:border-charcoal-800"
        className="h-12 text-[14px] text-slate-900 placeholder:text-slate-400 dark:text-slate-100 dark:placeholder:text-slate-500"
        value={query}
        onValueChange={setQuery}
      />
      <FilterTabs value={filter} onChange={setFilter} labels={filterLabels} />
      <CommandList>
        <CommandEmpty>{t('search_no_results', { defaultValue: 'Aucun résultat.' })}</CommandEmpty>

        {shops.length > 1 && showShopSwitcher && (
          <>
            <CommandGroup
              heading={t('section_switch_shop', { defaultValue: 'Changer de boutique' })}
            >
              {shops.map((shop) => (
                <CommandItem
                  key={`shop-${shop.id}`}
                  onSelect={() => {
                    selectShop(shop);
                    setOpen(false);
                  }}
                  keywords={[shop.name, 'boutique', 'shop', 'switch']}
                  className="min-h-10 sm:min-h-9"
                >
                  <Store className="mr-2 h-4 w-4 shrink-0" />
                  <span className="flex-1 truncate">{shop.name}</span>
                  {currentShop?.id === shop.id && (
                    <span className="ml-2 text-xs font-medium text-lime-600 dark:text-lime-400">
                      active
                    </span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {quickActions.length > 0 && filter !== 'pages' && (
          <>
            <CommandGroup heading={t('section_quick_actions', { defaultValue: 'Actions rapides' })}>
              {quickActions.map((action) => (
                <CommandRow
                  key={action.id}
                  action={action}
                  onSelect={() => handleSelect(action)}
                  showDescription={filter === 'tools'}
                />
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {showBrowseTools && browseToolsItems.length > 0 && (
          <CommandGroup
            heading={t('section_browse_tools', { defaultValue: 'Parcourir les outils' })}
          >
            {browseToolsItems.map((action) => (
              <CommandRow
                key={`browse-${action.id}`}
                action={action}
                onSelect={() => handleSelect(action)}
                showDescription
              />
            ))}
          </CommandGroup>
        )}

        {!showBrowseTools &&
          ORDERED_GROUPS.map(({ key, heading }) => {
            if (!allowedGroups.has(key)) return null;
            const items = groups[key];
            if (!items || items.length === 0) return null;
            // Skip duplicates already shown in quick actions
            const filtered = items.filter((action) => !action.quickAction);
            if (filtered.length === 0) return null;
            return (
              <CommandGroup key={key} heading={heading}>
                {filtered.map((action) => (
                  <CommandRow
                    key={action.id}
                    action={action}
                    onSelect={() => handleSelect(action)}
                    showDescription={filter === 'tools'}
                  />
                ))}
              </CommandGroup>
            );
          })}

        <CommandSeparator />
        <CommandGroup heading={t('shortcuts', { defaultValue: 'Shortcuts' })}>
          <CommandItem disabled>
            <Keyboard className="mr-2 h-4 w-4" />
            <span>{t('keyboard_shortcuts', { defaultValue: 'Raccourcis clavier' })}</span>
            <CommandShortcut>?</CommandShortcut>
          </CommandItem>
          <CommandItem disabled>
            <Search className="mr-2 h-4 w-4" />
            <span>{t('search', { defaultValue: 'Rechercher' })}</span>
            <CommandShortcut>⌘ K</CommandShortcut>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
